import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../services/api';
import { FiX, FiLoader, FiEdit3, FiRefreshCw, FiCheck, FiAlertTriangle } from 'react-icons/fi';
import { QualityScoreBadge, QualityGateIndicator } from './QualityScoreBadge';
import { ContentGoalSelector } from './ContentGoalSelector';

function CampaignCreationModal({ clientId, client, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [qualityGate, setQualityGate] = useState(null);

  const [objective, setObjective] = useState('');
  const [customObjective, setCustomObjective] = useState(''); // For custom objective text
  const [contentGoal, setContentGoal] = useState('balanced'); // V.E.E. optimization goal
  const [duration, setDuration] = useState({ start: '', end: '' });
  const [customDirection, setCustomDirection] = useState('');

  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  
  // Track which objective was used to generate current suggestions
  const [suggestionsObjective, setSuggestionsObjective] = useState('');
  
  // Editable fields after selection
  const [editedName, setEditedName] = useState('');
  const [editedAngle, setEditedAngle] = useState('');
  const [editedAngleDescription, setEditedAngleDescription] = useState('');
  const [isEdited, setIsEdited] = useState(false);
  
  // Regenerate with hint
  const [regenerateHint, setRegenerateHint] = useState('');

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Reset ALL state when client changes - prevents stale data across clients
  useEffect(() => {
    setStep(1);
    setLoading(false);
    setError('');
    setObjective('');
    setCustomObjective('');
    setContentGoal('balanced');
    setDuration({ start: '', end: '' });
    setCustomDirection('');
    setSuggestions([]);
    setSelectedSuggestion(null);
    setSuggestionsObjective('');
    setEditedName('');
    setEditedAngle('');
    setEditedAngleDescription('');
    setIsEdited(false);
    setRegenerateHint('');
    console.log('Client changed, campaign creation state reset for client:', clientId);
  }, [clientId]);

  const objectives = [
    { value: 'product_launch', label: 'Product Launch' },
    { value: 'brand_awareness', label: 'Brand Awareness' },
    { value: 'sales_promotion', label: 'Sales/Promotion' },
    { value: 'event_coverage', label: 'Event Coverage' },
    { value: 'thought_leadership', label: 'Thought Leadership' },
    { value: 'community_building', label: 'Community Building' },
    { value: 'seasonal', label: 'Seasonal Campaign' },
    { value: 'custom', label: 'Custom' },
  ];

  // CRITICAL: When objective changes, clear downstream generated content
  // This implements the "upstream changes invalidate downstream" principle
  const handleObjectiveChange = (newObjective) => {
    setObjective(newObjective);
    
    // Clear custom objective if not custom
    if (newObjective !== 'custom') {
      setCustomObjective('');
    }
    
    // Clear all downstream content when objective changes
    if (newObjective !== suggestionsObjective) {
      setSuggestions([]);
      setSelectedSuggestion(null);
      setEditedName('');
      setEditedAngle('');
      setEditedAngleDescription('');
      setIsEdited(false);
    }
  };
  
  // Get the effective objective (use custom text if custom is selected)
  const getEffectiveObjective = () => {
    if (objective === 'custom' && customObjective.trim()) {
      return customObjective.trim();
    }
    return objective;
  };

  // When going back to step 1, warn if suggestions exist and objective might change
  const handleBackToStep1 = () => {
    setStep(1);
    // Don't clear suggestions yet - they'll be cleared if objective actually changes
  };

  const handleGenerateSuggestions = async (userEdits = null, isRegeneration = false) => {
    const effectiveObjective = getEffectiveObjective();
    if (!effectiveObjective) return;
    
    // Validate custom objective if selected
    if (objective === 'custom' && !customObjective.trim()) {
      setError('Please enter your custom campaign objective.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.generateCampaignSuggestions({
        client_id: clientId,
        objective: effectiveObjective,
        custom_direction: customDirection || null,
        user_edits: userEdits,
        // Pass previous suggestions for avoiding repeats and learning
        previous_suggestions: isRegeneration ? suggestions : [],
        is_regeneration: isRegeneration
      });

      setSuggestions(response.suggestions);
      setQualityGate(response.quality_gate || null); // Store quality gate info
      setSuggestionsObjective(effectiveObjective); // Track which objective generated these
      setSelectedSuggestion(null);
      setIsEdited(false);
      setStep(2);
      
      // Show credits charged if any
      if (response.credits_charged > 0) {
        console.log(`Regeneration charged ${response.credits_charged} credits`);
      }
    } catch (err) {
      // Handle AI-specific errors
      if (err.isAIError) {
        setError(`${err.message}${err.canRetry ? ' Click "Generate Concepts" to try again.' : ''}`);
      } else {
        const detail = err.response?.data?.detail;
        const errorMessage = err.response?.data?.user_message || 
          detail?.message ||
          (typeof detail === 'string' ? detail : null) ||
          (Array.isArray(detail) ? detail.map(e => e.msg || e.message).join(', ') : null) ||
          'Failed to generate suggestions. Please try again.';
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    setSelectedSuggestion(suggestion);
    setEditedName(suggestion.name);
    setEditedAngle(suggestion.angle);
    setEditedAngleDescription(suggestion.angle_description);
    setIsEdited(false);
    setStep(3);
  };

  const handleEditChange = (field, value) => {
    if (field === 'name') setEditedName(value);
    if (field === 'angle') setEditedAngle(value);
    if (field === 'angleDescription') setEditedAngleDescription(value);
    
    // Check if anything changed from original
    const hasChanges = 
      (field === 'name' ? value : editedName) !== selectedSuggestion.name ||
      (field === 'angle' ? value : editedAngle) !== selectedSuggestion.angle ||
      (field === 'angleDescription' ? value : editedAngleDescription) !== selectedSuggestion.angle_description;
    
    setIsEdited(hasChanges);
  };

  const handleRegenerate = () => {
    // This is a regeneration - will be charged credits and previous suggestions stored for learning
    handleGenerateSuggestions({
      name: editedName,
      angle: editedAngle + ': ' + editedAngleDescription
    }, true); // true = is regeneration
  };

  const handleRegenerateWithHint = async () => {
    if (!regenerateHint.trim()) return;
    
    // Store previous suggestions for passing to API
    const prevSuggestions = [...suggestions];
    
    // Clear current suggestions
    setSuggestions([]);
    setSelectedSuggestion(null);
    
    // Use the hint as additional direction
    const hint = regenerateHint.trim();
    const originalDirection = customDirection;
    const combinedDirection = customDirection 
      ? `${customDirection}. Additional guidance: ${hint}`
      : hint;
    
    // Temporarily set custom direction for regeneration
    setCustomDirection(combinedDirection);
    
    setLoading(true);
    setError('');

    try {
      const effectiveObjective = getEffectiveObjective();
      const response = await api.generateCampaignSuggestions({
        client_id: clientId,
        objective: effectiveObjective,
        custom_direction: combinedDirection,
        user_edits: null,
        previous_suggestions: prevSuggestions,
        is_regeneration: true
      });

      setSuggestions(response.suggestions);
      setQualityGate(response.quality_gate || null); // Store quality gate info
      setSuggestionsObjective(effectiveObjective);
      setSelectedSuggestion(null);
      setIsEdited(false);
      setRegenerateHint('');
      
    } catch (err) {
      if (err.isAIError) {
        setError(`${err.message}${err.canRetry ? ' Click "Regenerate" to try again.' : ''}`);
      } else {
        const detail = err.response?.data?.detail;
        const errorMessage = err.response?.data?.user_message || 
          detail?.message ||
          (typeof detail === 'string' ? detail : null) ||
          'Failed to generate suggestions. Please try again.';
        setError(errorMessage);
      }
    } finally {
      // Restore original custom direction
      setCustomDirection(originalDirection);
      setLoading(false);
    }
  };

  const handleCreateCampaign = async () => {
    if (!editedName || !editedAngle) return;

    setLoading(true);
    setError('');

    try {
      await api.createCampaign({
        client_id: clientId,
        name: editedName,
        objective: objective,  // Always send the enum value, not the custom text
        content_goal: contentGoal,  // V.E.E. optimization goal
        creative_angle: editedAngle,
        creative_angle_description: editedAngleDescription,
        duration_start: duration.start || null,
        duration_end: duration.end || null,
        platforms: ['instagram', 'facebook', 'youtube'],
        brief: objective === 'custom' ? customObjective : null  // Store custom objective in brief field
      });

      onSuccess();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMessage = err.response?.data?.user_message || 
        detail?.message ||
        (typeof detail === 'string' ? detail : null) ||
        (Array.isArray(detail) ? detail.map(e => e.msg || e.message).join(', ') : null) ||
        'Failed to create campaign. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999]"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div 
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card rounded-xl w-full max-w-3xl shadow-2xl border border-border/50 flex flex-col"
        style={{ maxHeight: '85vh' }}
      >
        {/* Header - fixed */}
        <div className="flex-shrink-0 flex items-center justify-between p-5 border-b border-border/50">
          <div>
            <h2 className="text-xl font-bold text-foreground">Create New Campaign</h2>
            <p className="text-sm text-muted-foreground mt-1">for {client.name}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="close-modal">
            <FiX size={24} />
          </button>
        </div>

        {/* Progress indicator - fixed */}
        <div className="flex-shrink-0 px-5 py-3 border-b border-border/30">
          <div className="flex items-center space-x-2 text-sm">
            <span className={`px-3 py-1 rounded-full ${step >= 1 ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}>1. Objective</span>
            <span className="text-muted-foreground">→</span>
            <span className={`px-3 py-1 rounded-full ${step >= 2 ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}>2. Choose Concept</span>
            <span className="text-muted-foreground">→</span>
            <span className={`px-3 py-1 rounded-full ${step >= 3 ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}>3. Refine & Create</span>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-5">
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Step 1: Objective */}
          {step === 1 && (
            <div className="space-y-5">
              {/* Warning if suggestions exist but user is changing objective */}
              {suggestions.length > 0 && suggestionsObjective && objective !== suggestionsObjective && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-sm flex items-start">
                  <FiAlertTriangle className="mr-2 mt-0.5 flex-shrink-0" />
                  <span>You changed the objective. Click &ldquo;Generate Concepts&rdquo; to get new suggestions aligned with this objective.</span>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Campaign Objective *</label>
                <div className="grid grid-cols-2 gap-3">
                  {objectives.map(obj => (
                    <button
                      key={obj.value}
                      onClick={() => handleObjectiveChange(obj.value)}
                      data-testid={`objective-${obj.value}`}
                      className={`px-4 py-3 rounded-lg border-2 transition-colors text-left ${
                        objective === obj.value ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-primary/50 text-foreground'
                      }`}
                    >
                      {obj.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Objective Text Field */}
              {objective === 'custom' && (
                <div className="animate-in fade-in duration-200">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Describe your campaign objective *
                  </label>
                  <textarea 
                    value={customObjective} 
                    onChange={(e) => setCustomObjective(e.target.value)} 
                    rows={3}
                    className="w-full px-4 py-2 bg-secondary border border-border rounded-lg focus:ring-2 focus:ring-primary text-foreground placeholder:text-muted-foreground"
                    placeholder="e.g., Showcase our completed home theater projects and why clients should choose us for their next installation..."
                    data-testid="custom-objective-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Be specific about what you want to achieve with this campaign
                  </p>
                </div>
              )}

              {/* Content Goal Selection - V.E.E. Framework */}
              {objective && (
                <div className="animate-in fade-in duration-200 pt-2">
                  <div className="border-t border-border/30 pt-5">
                    <label className="block text-sm font-medium text-foreground mb-3">
                      What's your goal for this content?
                    </label>
                    <ContentGoalSelector 
                      value={contentGoal} 
                      onChange={setContentGoal} 
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Start Date (Optional)</label>
                  <input type="date" value={duration.start} onChange={(e) => setDuration(prev => ({ ...prev, start: e.target.value }))}
                    className="w-full px-4 py-2 bg-secondary border border-border rounded-lg focus:ring-2 focus:ring-primary text-foreground" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">End Date (Optional)</label>
                  <input type="date" value={duration.end} onChange={(e) => setDuration(prev => ({ ...prev, end: e.target.value }))}
                    className="w-full px-4 py-2 bg-secondary border border-border rounded-lg focus:ring-2 focus:ring-primary text-foreground" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Any specific direction in mind? (Optional)</label>
                <textarea value={customDirection} onChange={(e) => setCustomDirection(e.target.value)} rows={3}
                  className="w-full px-4 py-2 bg-secondary border border-border rounded-lg focus:ring-2 focus:ring-primary text-foreground placeholder:text-muted-foreground"
                  placeholder="e.g., Focus on sustainability messaging, target millennial moms..." />
              </div>
            </div>
          )}

          {/* Step 2: Choose Concept */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-foreground">Choose a Campaign Concept</h3>
                <button 
                  onClick={() => setStep(1)} 
                  className="text-sm text-primary hover:text-primary/80"
                >
                  ← Change objective
                </button>
              </div>
              <p className="text-sm text-muted-foreground mb-4">Each concept pairs a campaign name with a creative angle that work together.</p>
              
              {/* Show which objective these suggestions are for */}
              <div className="mb-4 px-3 py-2 bg-primary/10 rounded-lg text-sm text-primary flex items-center justify-between flex-wrap gap-2">
                <span>Generated for: <strong>{objectives.find(o => o.value === suggestionsObjective)?.label || suggestionsObjective}</strong></span>
                {/* Quality Gate Indicator */}
                <QualityGateIndicator qualityGate={qualityGate} itemCount={suggestions.length} />
              </div>
              
              <div className="space-y-4">
                {suggestions.map((suggestion, idx) => (
                  <button 
                    key={idx} 
                    onClick={() => handleSelectSuggestion(suggestion)}
                    data-testid={`suggestion-${idx}`}
                    className={`w-full p-5 rounded-xl border-2 transition-all text-left hover:border-primary/50 hover:shadow-md border-border bg-secondary ${
                      suggestion.recommendation === 'skip' ? 'opacity-60' : ''
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <span className="text-3xl">{suggestion.icon}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <h4 className="text-xl font-bold text-foreground">{suggestion.name}</h4>
                          <span className="px-2 py-0.5 bg-primary/20 text-primary rounded text-xs font-medium">
                            {suggestion.angle}
                          </span>
                          {/* Quality Score Badge */}
                          <QualityScoreBadge 
                            score={suggestion.marketing_score}
                            size="sm"
                          />
                        </div>
                        <p className="text-muted-foreground mb-3">{suggestion.angle_description}</p>
                        
                        {/* Score Reasoning */}
                        {suggestion.score_reasoning && suggestion.score_reasoning.verdict && (
                          <p className="text-xs text-primary mb-2 italic">💡 {suggestion.score_reasoning.verdict}</p>
                        )}
                        
                        <div className="flex flex-wrap gap-2 mb-2">
                          {suggestion.example_content_types?.map((type, i) => (
                            <span key={i} className="px-2 py-1 bg-card text-muted-foreground rounded text-xs">{type}</span>
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground italic">{suggestion.why_it_works}</p>
                      </div>
                      <FiCheck className="text-muted-foreground mt-1" size={20} />
                    </div>
                  </button>
                ))}
              </div>

              {/* Regenerate Section */}
              <div className="mt-6 pt-4 border-t border-border">
                <div className="p-4 bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl">
                  <h4 className="font-semibold text-amber-400 mb-2 flex items-center">
                    <FiRefreshCw className="mr-2" /> Not what you&apos;re looking for?
                  </h4>
                  <p className="text-sm text-amber-400/80 mb-3">Get fresh suggestions or add a hint to guide the AI</p>
                  
                  {/* Quick Regenerate Button */}
                  <div className="flex gap-3 mb-3">
                    <button
                      onClick={() => handleGenerateSuggestions(null, true)}
                      disabled={loading}
                      className="flex items-center gap-2 px-4 py-2 bg-amber-500/20 text-amber-400 rounded-lg hover:bg-amber-500/30 disabled:opacity-50 font-medium border border-amber-500/30"
                      data-testid="quick-regenerate-btn"
                    >
                      {loading ? <FiLoader className="animate-spin" /> : <FiRefreshCw />}
                      <span>{loading ? 'Generating...' : 'Regenerate All'}</span>
                    </button>
                  </div>
                  
                  {/* Regenerate with Hint */}
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={regenerateHint}
                      onChange={(e) => setRegenerateHint(e.target.value)}
                      placeholder="e.g., Make it more playful, focus on urgency, add emotional appeal..."
                      className="flex-1 px-4 py-2 border border-amber-500/30 rounded-lg focus:ring-2 focus:ring-amber-500 bg-secondary text-foreground text-sm placeholder:text-muted-foreground"
                      data-testid="regenerate-hint-input"
                    />
                    <button
                      onClick={() => handleRegenerateWithHint()}
                      disabled={loading || !regenerateHint.trim()}
                      className="flex items-center gap-2 px-4 py-2 bg-secondary text-foreground rounded-lg hover:bg-secondary/80 disabled:opacity-50 font-medium border border-border"
                      data-testid="regenerate-with-hint-btn"
                    >
                      {loading ? <FiLoader className="animate-spin" /> : <FiRefreshCw />}
                      <span>With Hint</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Refine & Create */}
          {step === 3 && selectedSuggestion && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-foreground">Refine Your Campaign</h3>
                <button 
                  onClick={() => setStep(2)} 
                  className="text-sm text-primary hover:text-primary/80"
                >
                  ← Choose different concept
                </button>
              </div>
              <p className="text-sm text-muted-foreground">Edit the name or angle to add your own context. If you make changes, you can regenerate new suggestions based on your edits.</p>

              <div className="bg-secondary rounded-xl p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    <FiEdit3 className="inline mr-1" /> Campaign Name
                  </label>
                  <input 
                    type="text" 
                    value={editedName} 
                    onChange={(e) => handleEditChange('name', e.target.value)}
                    data-testid="edit-campaign-name"
                    className="w-full px-4 py-3 text-lg font-semibold border border-border rounded-lg focus:ring-2 focus:ring-primary bg-card text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    <FiEdit3 className="inline mr-1" /> Creative Angle
                  </label>
                  <input 
                    type="text" 
                    value={editedAngle} 
                    onChange={(e) => handleEditChange('angle', e.target.value)}
                    data-testid="edit-creative-angle"
                    className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary bg-card text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    <FiEdit3 className="inline mr-1" /> Angle Description
                  </label>
                  <textarea 
                    value={editedAngleDescription} 
                    onChange={(e) => handleEditChange('angleDescription', e.target.value)}
                    rows={3}
                    data-testid="edit-angle-description"
                    className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary bg-card text-foreground"
                  />
                </div>

                {isEdited && (
                  <div className="flex items-center justify-between p-4 bg-primary/10 border border-primary/30 rounded-lg">
                    <div className="text-sm text-primary">
                      <strong>You&apos;ve made edits.</strong> Regenerate to get new AI suggestions incorporating your changes.
                    </div>
                    <button
                      onClick={handleRegenerate}
                      disabled={loading}
                      data-testid="regenerate-btn"
                      className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
                    >
                      <FiRefreshCw className={loading ? 'animate-spin' : ''} />
                      Regenerate (2 credits)
                    </button>
                  </div>
                )}
              </div>

              {/* Preview */}
              <div className="border border-border rounded-xl p-5 bg-card">
                <h4 className="text-sm font-medium text-muted-foreground mb-3">CAMPAIGN PREVIEW</h4>
                <div className="flex items-start gap-4">
                  <span className="text-3xl">{selectedSuggestion.icon}</span>
                  <div>
                    <h3 className="text-xl font-bold text-foreground">{editedName}</h3>
                    <span className="inline-block px-2 py-0.5 bg-primary/20 text-primary rounded text-sm font-medium mt-1">
                      {editedAngle}
                    </span>
                    <p className="text-muted-foreground mt-2">{editedAngleDescription}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer - fixed at bottom */}
        <div className="flex-shrink-0 flex items-center justify-end space-x-3 p-5 border-t border-border bg-card rounded-b-xl">
          <button onClick={onClose} className="px-6 py-2 text-muted-foreground hover:text-foreground">Cancel</button>
          
          {step === 1 && (
            <button 
              onClick={() => handleGenerateSuggestions()} 
              disabled={!objective || (objective === 'custom' && !customObjective.trim()) || loading}
              data-testid="generate-suggestions-btn"
              className="flex items-center space-x-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
            >
              {loading && <FiLoader className="animate-spin" />}
              <span>{loading ? 'Generating...' : 'Generate Campaign Ideas'}</span>
            </button>
          )}
          
          {step === 3 && (
            <button 
              onClick={handleCreateCampaign} 
              disabled={!editedName || !editedAngle || loading}
              data-testid="create-campaign-btn"
              className="flex items-center space-x-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {loading && <FiLoader className="animate-spin" />}
              <span>{loading ? 'Creating...' : 'Create Campaign'}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

export default CampaignCreationModal;
